import { ChartContainer } from "@components/charts/ChartContainer";
import { usePaginatedRequest } from "@components/hooks/usePaginatedRequest";
import { range, Solana, SolanaTransactionInfo } from "@glow/shared";
import { ButtonSwitcher } from "@lux/atoms/components/ButtonSwitcher";
import { LuxEmptyState } from "@lux/atoms/components/LuxEmptyState";
import { GlowErrorState } from "@glow/react/components/GlowErrorState";
import { TransactionActivityRow } from "@glow/react/components/activity/TransactionActivityRow";
import { SparkChart } from "@glow/react/components/charts/SparkChart";
import { InformationLoadingRow } from "@glow/react/components/layout/InformationLoadingRow";
import { useLoadMoreObserver } from "@lux/atoms/hooks/useLoadMoreObserver";
import { usePolling } from "@lux/atoms/hooks/usePolling";
import { FRAMER_EASE } from "@lux/atoms/utils/framer";
import { ResponsiveBreakpoint } from "@lux/atoms/utils/StyleConstants";
import ChartIcon from "@lux/icons/feather/bar-chart.svg";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { DateTime } from "luxon";
import React, { useState } from "react";
import { CollectionAttribute } from "./CollectionAttribute";
import { useCollectionPageContext } from "./CollectionPageContext";
import { CollectionTabBar } from "./CollectionTabBar";
import { solAmountString } from "@glow/crypto";

type DataPoint = { timestamp: string; value: number };

const ActivityCharts = () => {
  const {
    data,
    pricePoints,
    volumePoints,
    priceData,
    pricePointsHourly,
    volumePointsHourly,
  } = useCollectionPageContext();
  const avgPrice = data.collection.avg_price_30d_sol!;
  const avgVolume = data.collection.volume_30d_sol!;

  const [period, setPeriod] = useState<"daily" | "hourly">("daily");
  const [avgPriceTooltip, setAvgPriceTooltip] = useState<DataPoint | null>(
    null,
  );
  const [avgVolumeTooltip, setAvgVolumeTooltip] = useState<DataPoint | null>(
    null,
  );

  if (!priceData) {
    return null;
  }

  return (
    <div className={"mb-3"}>
      <div className="mb-2 flex">
        <ButtonSwitcher
          options={[
            { value: "daily", label: "Daily" },
            { value: "hourly", label: "Hourly" },
          ]}
          setValue={(value) => setPeriod(value)}
          value={period}
          smallSizeBreakpoint={"always"}
        />
      </div>

      <div className="flex-column rounded activities">
        <div className="p-3 chart activity">
          <div className="chart-title mb-2 spread">
            <CollectionAttribute
              label="Sale Price"
              value={solAmountString(avgPrice)}
            />

            {avgPriceTooltip && (
              <CollectionAttribute
                label={DateTime.fromISO(
                  avgPriceTooltip.timestamp,
                ).toLocaleString(
                  period === "daily"
                    ? {
                        month: "long",
                        day: "numeric",
                      }
                    : {
                        weekday: "long",
                        hour: "numeric",
                        minute: "2-digit",
                      },
                )}
                value={solAmountString(avgPriceTooltip.value)}
                className={"text-right mono-number"}
              />
            )}
          </div>
          <ChartContainer>
            {({ width }) => (
              <SparkChart
                width={width}
                height={100}
                points={period === "daily" ? pricePoints! : pricePointsHourly!}
                showPointerLine={true}
                handlePointChange={setAvgPriceTooltip}
              />
            )}
          </ChartContainer>
        </div>
        <div className="p-3 chart volume">
          <div className="chart-title mb-2 spread">
            <CollectionAttribute
              label="Volume"
              value={solAmountString(avgVolume)}
            />

            {avgVolumeTooltip && (
              <CollectionAttribute
                label={DateTime.fromISO(
                  avgVolumeTooltip.timestamp,
                ).toLocaleString(
                  period === "daily"
                    ? {
                        month: "long",
                        day: "numeric",
                      }
                    : {
                        weekday: "long",
                        hour: "numeric",
                        minute: "2-digit",
                      },
                )}
                value={solAmountString(avgVolumeTooltip.value)}
                className={"text-right mono-number"}
              />
            )}
          </div>
          <ChartContainer>
            {({ width }) => (
              <SparkChart
                type="bar"
                width={width}
                height={100}
                points={
                  period === "daily" ? volumePoints! : volumePointsHourly!
                }
                showPointerLine={true}
                handlePointChange={setAvgVolumeTooltip}
              />
            )}
          </ChartContainer>
        </div>
      </div>

      <style jsx>{`
        .activities {
          border: 1px solid var(--secondary-border-color);

          .chart {
            &:first-child {
              border-right: none;
              border-bottom: 1px solid var(--secondary-border-color);
            }
          }
        }
      `}</style>
    </div>
  );
};

// TODO: link each row to the nft detail page
const NftSaleRow = ({
  tx,
  previousTx,
}: {
  tx: SolanaTransactionInfo.TransactionInfo;
  previousTx?: SolanaTransactionInfo.TransactionInfo;
}) => {
  return (
    <TransactionActivityRow
      key={tx.api_id}
      mode={"nft-collection-sales"}
      stickyHeader
      sectionClassName={"px-3 section"}
      categoryClassName={"px-3 mt-2"}
      rowClassName={"px-3 py-1"}
      transactionInfo={tx}
      prevTransactionInfo={previousTx}
    />
  );
};

const ActivityTransactions = () => {
  const { data } = useCollectionPageContext();
  const { entries, fetching, status, loadMore, fetchFirstPage } =
    usePaginatedRequest({
      path: "/solana/nft/get-collection-transaction-infos",
      args: {
        solana_nft_collection_api_id: data.collection.api_id,
        network: Solana.Network.Mainnet,
      },
      pageSize: 10,
    });

  const loadMoreDiv = useLoadMoreObserver({
    loadMore,
    depArray: [status, fetching],
  });

  usePolling(() => {
    fetchFirstPage();
  }, 1_000);

  return (
    <div className="transactions">
      {status === "no-data" && (
        <LuxEmptyState
          icon={<ChartIcon />}
          title={"No Sales Found"}
          desc={"We may still be processing this collection."}
        />
      )}

      {/* TODO: be smarter about not animating entries at the bottom or initial entries */}
      <AnimatePresence>
        {entries.map((tx, idx) => (
          <motion.div
            key={tx.api_id}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
              height: { delay: 0, duration: 0.15, ease: FRAMER_EASE },
              opacity: { delay: 0.08, ease: FRAMER_EASE },
            }}
          >
            <NftSaleRow
              tx={tx}
              previousTx={idx > 0 ? entries[idx - 1] : undefined}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {fetching ? (
        <div className="px-2">
          {range(10).map((i) => (
            <InformationLoadingRow key={i} className={"py-2"} />
          ))}
        </div>
      ) : (
        status === "error" && <GlowErrorState onRetry={loadMore} />
      )}

      {loadMoreDiv}

      <style jsx>{`
        .transactions {
          margin-left: -0.5rem;
          width: calc(100% + 1rem);
        }
      `}</style>
    </div>
  );
};

export const CollectionSales = () => {
  const { emptyCollectionFilters } = useCollectionPageContext();

  return (
    <div className="sales">
      <CollectionTabBar />

      <div
        className={classNames("sales-inner flex spread", {
          "empty-filters": emptyCollectionFilters,
        })}
      >
        <div className="charts">
          <ActivityCharts />
        </div>
        <div className="transactions">
          <ActivityTransactions />
        </div>
      </div>

      <style jsx>{`
        .sales {
          .sales-inner {
            margin-left: calc(var(--sidebar-width) + var(--side-padding));
            margin-top: 1.5rem;

            &.empty-filters {
              margin-left: 0;
            }

            @media (max-width: 1584px) {
              margin-left: 0;
            }

            @media (max-width: ${ResponsiveBreakpoint.medium}) {
              flex-direction: column;
            }

            .charts,
            .transactions {
              width: calc(50% - 1rem);

              @media (max-width: ${ResponsiveBreakpoint.medium}) {
                width: 100%;
              }
            }
          }
        }
      `}</style>
    </div>
  );
};
